// Auto-generated industries data — DO NOT EDIT MANUALLY
// Slugs: mortgage-lending, veterinary-pet-care, chiropractic-pt, solar-energy
import type { Industry } from '../types';

export const industriesGroup5: Industry[] = [
  {
    "slug": "mortgage-lending",
    "name": "Mortgage & Lending",
    "icon": "Bank",
    "tagline": "Never Miss a Rate Inquiry — 24/7 AI Lead Response",
    "description": "BizRnR is the 24/7 AI receptionist for mortgage brokers — it answers every rate inquiry the moment it comes in, after hours included, qualifies borrowers, and books consultations automatically so you never miss a lead.",
    "seoTitle": "24/7 AI Lead Response for Mortgage Brokers — Never Miss",
    "seoDescription": "The 24/7 AI receptionist for mortgage brokers: BizRnR answers every rate inquiry after hours, qualifies borrowers, and books consultations so you never miss a lead. $99/mo, 30-day money-back guarantee.",
    "longDescription": "In mortgage lending, response time directly correlates to funded loans. Borrowers who receive a callback within 5 minutes are 100x more likely to proceed with that lender compared to those contacted after an hour. BizRnR gives mortgage lenders, brokers, and loan officers an AI phone agent that contacts every lead within seconds and qualifies them for the right loan product.\n\nBizRnR's AI engages prospects with questions about purchase vs refinance, property type, estimated purchase price or current loan balance, credit score range, and employment status. Based on these inputs, the AI provides a preliminary assessment of loan options and books a detailed consultation with the appropriate loan officer.\n\nFor refinance campaigns, BizRnR calls your database of past clients when rates drop, qualifying them for potential savings and scheduling applications. This proactive approach captures rate-driven refinance volume before competitors reach the same borrowers.\n\nPipeline management is streamlined: BizRnR follows up with borrowers who started an application but didn't complete it, touches base on pending appraisals and underwriting milestones, and confirms closing appointments. This automated pipeline nurturing reduces fallout by 15-20%.\n\nBizRnR integrates with Encompass, Byte, MortgageFlex, and other LOS platforms.",
    "painPoints": [
      {
        "title": "Rate Shoppers Move Fast",
        "description": "Borrowers contact 4-5 lenders simultaneously. Sub-60-second response wins."
      },
      {
        "title": "Refinance Lead Timing",
        "description": "When rates drop, the 72-hour window requires instant outreach."
      },
      {
        "title": "Pre-Qual Drop-Off",
        "description": "Borrowers start the pre-qual process and abandon it."
      }
    ],
    "solutions": [
      {
        "title": "Sub-60-Second Lead Response",
        "description": "AI contacts every inquiry within 60 seconds."
      },
      {
        "title": "Refinance Rate Drop Campaigns",
        "description": "When rates fall, BizRnR contacts past borrowers automatically."
      },
      {
        "title": "Pre-Qual Completion Follow-Up",
        "description": "AI follows up with every incomplete application."
      }
    ],
    "stats": [
      {
        "value": "60s",
        "label": "Lead response time"
      },
      {
        "value": "5.8x",
        "label": "More consultations booked"
      },
      {
        "value": "24/7",
        "label": "Availability for borrower inquiries"
      }
    ],
    "useCases": [
      {
        "title": "Fed Rate Cut Refi Blitz",
        "description": "Fed cuts rates Wednesday. By Thursday BizRnR has contacted every past borrower."
      },
      {
        "title": "First-Time Buyer Lead",
        "description": "Buyer submits rate inquiry at 9pm. BizRnR calls in 60 seconds and books consultation."
      }
    ],
    "faqs": [
      {
        "question": "Is BizRnR RESPA compliant?",
        "answer": "BizRnR provides communication tools; consult your compliance officer."
      },
      {
        "question": "Can it pull credit reports?",
        "answer": "No, credit pulls happen within your LOS."
      },
      {
        "question": "Does it integrate with loan origination software?",
        "answer": "Yes, integrates with major LOS platforms."
      },
      {
        "question": "Can I use it for commercial lending?",
        "answer": "Yes, configure separate flows for residential and commercial."
      }
    ],
    "keywords": [
      "mortgage lead automation",
      "loan officer AI",
      "refinance lead software"
    ]
  },
  {
    "slug": "veterinary-pet-care",
    "name": "Veterinary & Pet Care",
    "icon": "PawPrint",
    "tagline": "More Appointments. Fewer No-Shows. Happier Clients.",
    "description": "BizRnR helps veterinary clinics automate appointment booking, reminders, and wellness visit reactivation.",
    "longDescription": "Veterinary practices and pet care businesses deal with a unique challenge: pet emergencies are urgent, emotional, and happen at all hours. When a pet owner calls at midnight because their dog ate something toxic, they need immediate guidance and an appointment — not voicemail. BizRnR provides veterinary practices with an AI that handles these sensitive calls with empathy and efficiency.\n\nBizRnR's AI triages calls by urgency: true emergencies (poisoning, trauma, breathing difficulty) are immediately routed to your emergency line or the nearest 24-hour animal hospital, while routine appointment requests (wellness exams, vaccinations, grooming) are scheduled in your practice management system.\n\nNew client acquisition is a major revenue driver for vet practices, and BizRnR captures every new pet parent inquiry. The AI collects pet species, breed, age, and health concerns, then books the first wellness exam. For specialty practices (dermatology, orthopedics, oncology), the AI gathers referral information and prior treatment history.\n\nPreventive care reminders generate consistent revenue: BizRnR calls clients when their pets are due for vaccinations, heartworm testing, dental cleanings, or annual wellness exams. These proactive calls keep your appointment book full and your patients healthy.\n\nFor boarding and daycare services, BizRnR handles reservation inquiries, collects vaccination records, and confirms pickup/drop-off times.",
    "painPoints": [
      {
        "title": "Appointment No-Shows",
        "description": "Vet no-shows are costly. Consistent reminders are the solution."
      },
      {
        "title": "Wellness Visits Overdue",
        "description": "Thousands of pets are overdue for annual wellness visits."
      },
      {
        "title": "New Patient Acquisition",
        "description": "New pet owners need a vet but finding and booking should not require manual effort."
      }
    ],
    "solutions": [
      {
        "title": "Appointment Reminder Sequences",
        "description": "AI sends voice and SMS reminders at 72h, 24h, and 2h."
      },
      {
        "title": "Wellness Visit Reactivation",
        "description": "AI identifies overdue patients and sends personalized reminders."
      },
      {
        "title": "New Patient Welcome Campaigns",
        "description": "New pet owners in your area receive a welcome offer from BizRnR."
      }
    ],
    "stats": [
      {
        "value": "69%",
        "label": "Fewer no-shows"
      },
      {
        "value": "2.6x",
        "label": "Wellness visit bookings"
      },
      {
        "value": "88%",
        "label": "New patient conversion"
      }
    ],
    "useCases": [
      {
        "title": "Annual Wellness Overdue",
        "description": "A dog's last exam was 14 months ago. BizRnR sends a friendly reminder."
      },
      {
        "title": "New Puppy Owner Outreach",
        "description": "New pet owner registers a puppy. BizRnR follows up with first appointment info."
      }
    ],
    "faqs": [
      {
        "question": "Does BizRnR work with veterinary software?",
        "answer": "Yes, integrates with major veterinary PMS platforms."
      },
      {
        "question": "Can the AI handle emergency triage?",
        "answer": "BizRnR handles routine booking and escalates emergencies."
      },
      {
        "question": "Can reminders include pet names?",
        "answer": "Yes, all reminders are personalized with the pet's name."
      },
      {
        "question": "Does it work for boarding and grooming?",
        "answer": "Yes, configure separate booking flows for each service."
      }
    ],
    "keywords": [
      "veterinary automation",
      "vet appointment AI",
      "pet care booking software"
    ]
  },
  {
    "slug": "chiropractic-pt",
    "name": "Chiropractic & Physical Therapy",
    "icon": "Bone",
    "tagline": "Fill Your Treatment Schedule. Reduce Drop-Off Rates.",
    "description": "BizRnR helps chiropractic and PT practices automate new patient intake, reminders, and re-engagement for dropped-off patients.",
    "longDescription": "Chiropractic offices and physical therapy practices depend on consistent patient volume and high visit compliance to maintain revenue. When a patient misses appointments or drops off their treatment plan, it hurts both outcomes and income. BizRnR ensures your practice captures every new patient inquiry and keeps existing patients on track with their care plans.\n\nBizRnR's AI handles new patient intake calls by collecting symptoms, injury history, insurance information, and scheduling preferences. For auto accident and workers' comp patients, the AI gathers claim details and attorney information upfront, streamlining the documentation process.\n\nAppointment compliance is dramatically improved through BizRnR's automated reminder and follow-up calls. When patients miss appointments, the AI calls within 2 hours to reschedule — reducing patient dropout by 35%. For patients completing their initial treatment plan, BizRnR follows up to schedule maintenance visits, driving long-term retention.\n\nReferral coordination with primary care physicians, orthopedists, and pain management specialists is handled professionally. BizRnR can receive referral calls, collect patient information, and schedule initial evaluations without your front desk lifting a finger.\n\nFor practices offering wellness services (massage therapy, acupuncture, nutritional counseling), BizRnR cross-sells these services to existing patients during follow-up calls.",
    "painPoints": [
      {
        "title": "New Patient Inquiry Drop-Off",
        "description": "Patients in pain shop fast. Slow follow-up loses them to the first provider who calls."
      },
      {
        "title": "Treatment Plan Non-Completion",
        "description": "Patients miss appointments and drop off, hurting revenue and health outcomes."
      },
      {
        "title": "Insurance Verification Delays",
        "description": "Waiting for verification loses patients to faster competitors."
      }
    ],
    "solutions": [
      {
        "title": "Instant New Patient Response",
        "description": "AI calls every new inquiry within 60 seconds."
      },
      {
        "title": "Treatment Plan Adherence Reminders",
        "description": "Automated reminders keep patients on their plan and coming back."
      },
      {
        "title": "Streamlined Intake Automation",
        "description": "AI collects insurance info upfront, speeding up verification."
      }
    ],
    "stats": [
      {
        "value": "4.3x",
        "label": "New patient conversion"
      },
      {
        "value": "77%",
        "label": "Treatment plan completion"
      },
      {
        "value": "52%",
        "label": "Fewer missed appointments"
      }
    ],
    "useCases": [
      {
        "title": "Injury Inquiry Response",
        "description": "Patient injures back Monday night. BizRnR is first to call Tuesday and books Wednesday."
      },
      {
        "title": "Treatment Drop-Off Recovery",
        "description": "Patient misses 3rd appointment. BizRnR calls with a rescheduling offer."
      }
    ],
    "faqs": [
      {
        "question": "Is BizRnR HIPAA-ready?",
        "answer": "BizRnR follows strict security standards. Consult your compliance team."
      },
      {
        "question": "Can it handle insurance pre-authorization?",
        "answer": "BizRnR collects insurance details; your staff handles pre-auth."
      },
      {
        "question": "Does it work for multi-location practices?",
        "answer": "Yes, manage multiple locations from one account."
      },
      {
        "question": "Can AI send post-appointment surveys?",
        "answer": "Yes, automated satisfaction surveys after each visit."
      }
    ],
    "keywords": [
      "chiropractic automation",
      "physical therapy AI",
      "PT scheduling software"
    ]
  },
  {
    "slug": "solar-energy",
    "name": "Solar & Energy",
    "icon": "Sun",
    "tagline": "Respond Faster. Close More Solar Contracts.",
    "description": "BizRnR gives solar companies an AI assistant that responds to inquiries instantly, qualifies homeowners, and books site assessments.",
    "longDescription": "Solar energy companies face a unique sales challenge: long sales cycles, high customer education requirements, and intense competition for every qualified lead. When a homeowner expresses interest in solar panels, the company that responds first with relevant information wins the consultation — and likely the $25,000-$50,000 installation contract. BizRnR gives solar companies an AI sales agent that engages every lead instantly.\n\nBizRnR's AI qualifies solar prospects by asking about property ownership, roof age and condition, current electric bill, shade coverage, and HOA restrictions. This pre-qualification saves your sales team hours by filtering out renters, properties with unsuitable roofs, and other non-viable prospects before an in-person consultation is scheduled.\n\nFor canvassing operations, BizRnR supercharges door-to-door results: every homeowner who shows interest at the door gets a professional follow-up call within hours. This call confirms the consultation appointment, answers initial questions, and sends preparatory materials — increasing consultation show rates by 40%.\n\nPost-consultation follow-up is automated for the lengthy consideration period typical in solar sales. BizRnR touches base at strategic intervals with personalized messaging about current incentives, tax credits, and financing options. This nurture sequence converts an additional 15-20% of prospects who would otherwise go cold.\n\nBizRnR integrates with Salesforce, Aurora Solar, and other solar CRM/design platforms.",
    "painPoints": [
      {
        "title": "Solar Lead Speed is Everything",
        "description": "Homeowners contact 5 companies simultaneously. Response time is the differentiator."
      },
      {
        "title": "High-Ticket Sales Need Multiple Touchpoints",
        "description": "Solar is a 20k+ decision. Without follow-up, prospects go dark."
      },
      {
        "title": "Site Assessment No-Shows",
        "description": "Qualified appointments are ghosted, wasting your installer time."
      }
    ],
    "solutions": [
      {
        "title": "Sub-60-Second Lead Response",
        "description": "AI calls every inquiry within 60 seconds, qualifying roof size and utility bill."
      },
      {
        "title": "Multi-Touch Nurture Sequences",
        "description": "Automated 8-touch follow-up keeps your brand top-of-mind."
      },
      {
        "title": "Site Assessment Confirmation",
        "description": "Multi-touchpoint sequences ensure high show rates."
      }
    ],
    "stats": [
      {
        "value": "60s",
        "label": "Lead response time"
      },
      {
        "value": "3.9x",
        "label": "More assessments booked"
      },
      {
        "value": "61%",
        "label": "Higher close rate"
      }
    ],
    "useCases": [
      {
        "title": "Weekend Solar Lead",
        "description": "Homeowner submits inquiry Saturday afternoon. BizRnR calls in 60 seconds and books a Tuesday assessment."
      },
      {
        "title": "Cold Lead Reactivation",
        "description": "90-day-old leads get a fresh outreach with updated incentive information."
      }
    ],
    "faqs": [
      {
        "question": "Can BizRnR ask about utility bills and roof age?",
        "answer": "Yes, configure AI to collect any qualifying information."
      },
      {
        "question": "Does it work for commercial solar?",
        "answer": "Yes, separate flows for residential and commercial."
      },
      {
        "question": "Can it explain solar incentives?",
        "answer": "Yes, BizRnR communicates incentive information from your knowledge base."
      },
      {
        "question": "Does it integrate with solar CRMs?",
        "answer": "Yes, integrates with major solar CRM platforms."
      }
    ],
    "keywords": [
      "solar lead automation",
      "solar AI",
      "solar company software"
    ]
  }
];
